import React, { useState, useEffect, useCallback } from "react";
import {
  ClipboardList, PlayCircle, FileText, ChevronRight,
} from "lucide-react";
import Badge from "../../common/Badge";
import { statusVariant } from "../../../utils/statusVariant";
import Toast, { ToastMessage } from "../../common/Toast";
import Skeleton from "../../common/Skeleton";
import NoDataFound from "../../common/NoDataFound";
import sopInstanceService, {
  type MySop,
  type ProcessInstance,
} from "../../../services/sopInstanceService";
import InitiateSopModal from "./InitiateSopModal";
import SopInstanceDetailModal from "./SopInstanceDetailModal";
import SopStepsPreviewModal from "./SopStepsPreviewModal";
import { isClosedInstance, visibleForScope } from "../../../views/member/work/workArchive";

type Section = "my_sops" | "initiated";

// Which section(s) to expose:
//  "both"      (default) — original two-section nav (My SOPs + Initiated).
//  "catalogue" — only the "My SOPs" template list (now lives on the Identity page).
//  "instances" — only invoked/initiated SOPs (the Action Centre tab jumps here).
type MySopsView = "instances" | "catalogue" | "both";

interface MySopsTabProps {
  scope?: "active" | "archive";
  readOnly?: boolean;
  view?: MySopsView;
}

// ── Skeleton placeholder — mirrors the real list-row geometry so there is no
//    layout jump when data lands (status icon · title+meta+progress · chevron).
const RowSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <div className="space-y-2" aria-hidden="true">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="bg-white border border-gray-200 rounded-xl px-4 py-3"
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div className="flex items-center gap-2">
              <Skeleton.Box width="45%" height={13} />
              <Skeleton.Pill width={64} height={16} style={{ marginLeft: "auto" }} />
            </div>
            <Skeleton.Box width="60%" height={10} />
            {/* progress bar */}
            <div className="flex items-center gap-2" style={{ marginTop: 4 }}>
              <Skeleton.Box width="100%" height={6} radius={9999} style={{ flex: 1 }} />
              <Skeleton.Box width={28} height={10} />
            </div>
          </div>
          <Skeleton.Box width={14} height={14} style={{ marginTop: 4 }} />
        </div>
      </div>
    ))}
  </div>
);

const MySopsTab: React.FC<MySopsTabProps> = ({ scope = "active", readOnly = false, view = "both" }) => {
  // Archive scope has no catalogue (it isn't a record). "instances" view also
  // suppresses the catalogue; "catalogue" view suppresses instances. The inner
  // side-nav only appears when BOTH sections are available.
  const showCatalogue = view !== "instances" && scope !== "archive";
  const showInstances = view !== "catalogue";
  const showNav = showCatalogue && showInstances;
  // In archive scope the catalogue ("my_sops") isn't a record, so we lock to the
  // instances section and only ever show closed instances.
  const [section, setSection] = useState<Section>(showCatalogue ? "my_sops" : "initiated");
  const [mySops, setMySops] = useState<MySop[]>([]);
  const [instances, setInstances] = useState<ProcessInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [openInitiateId, setOpenInitiateId] = useState<number | null>(null);
  const [openInitiateName, setOpenInitiateName] = useState("");
  const [openInstanceId, setOpenInstanceId] = useState<number | null>(null);
  const [openPreviewId, setOpenPreviewId] = useState<number | null>(null);
  const [openPreviewName, setOpenPreviewName] = useState("");
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const loadMySops = useCallback(async () => {
    setLoading(true);
    try {
      const data = await sopInstanceService.listMySops();
      setMySops(data);
    } catch {
      setToast({ type: "error", message: "Failed to load SOPs." });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInstances = useCallback(async () => {
    setLoading(true);
    try {
      const [tasks, initiated] = await Promise.all([
        sopInstanceService.listInstances("my_tasks"),
        sopInstanceService.listInstances("my_initiated"),
      ]);
      // Merge, deduplicate by id
      const map = new Map<number, ProcessInstance>();
      [...tasks, ...initiated].forEach((i) => map.set(i.id, i));
      setInstances(Array.from(map.values()).sort(
        (a, b) => new Date(b.initiated_on ?? 0).getTime() - new Date(a.initiated_on ?? 0).getTime()
      ));
    } catch {
      setToast({ type: "error", message: "Failed to load instances." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (section === "my_sops") loadMySops();
    else loadInstances();
  }, [section, loadMySops, loadInstances]);

  const filteredSops = mySops;

  const filteredInstances = visibleForScope(instances, (i) => isClosedInstance(i.status), scope);

  return (
    <>
      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}

      {/* Initiate modal — never opens in readOnly */}
      {!readOnly && openInitiateId != null && (
        <InitiateSopModal
          processId={openInitiateId}
          processName={openInitiateName}
          onClose={() => setOpenInitiateId(null)}
          setToast={setToast}
          onSuccess={() => {
            setOpenInitiateId(null);
            if (showInstances) {
              setSection("initiated");
              loadInstances();
            } else {
              // Catalogue-only (Identity page): instances live in the Action
              // Centre's Compliance Process tab now, so just confirm.
              setToast({
                type: "success",
                message: "SOP initiated — track it under Compliance Process in the Action Centre.",
              });
            }
          }}
        />
      )}

      {/* Instance detail modal */}
      {openInstanceId != null && (
        <SopInstanceDetailModal
          instanceId={openInstanceId}
          onClose={() => setOpenInstanceId(null)}
          onRefreshParent={loadInstances}
          readOnly={readOnly}
        />
      )}

      {/* Dry-run steps preview (template) — shows all steps, mine highlighted */}
      {openPreviewId != null && (
        <SopStepsPreviewModal
          processId={openPreviewId}
          processName={openPreviewName}
          onClose={() => setOpenPreviewId(null)}
        />
      )}

      <div className="flex h-full min-h-0">
        {/* ── Side nav (only when both sections are available) ── */}
        {showNav && (
          <div className="w-48 flex-shrink-0 bg-gray-50 border-r border-gray-200 py-4 px-2 space-y-1">
            <button
              onClick={() => setSection("my_sops")}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition ${
                section === "my_sops"
                  ? "bg-white shadow-sm text-blue-600 font-semibold border border-gray-200"
                  : "text-gray-600 hover:bg-white hover:text-gray-800"
              }`}
            >
              <ClipboardList size={15} />
              My SOPs
              {mySops.length > 0 && (
                <span className="ml-auto text-[10px] bg-gray-200 text-gray-600 rounded-full px-1.5 py-0.5 font-bold">
                  {mySops.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setSection("initiated")}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition ${
                section === "initiated"
                  ? "bg-white shadow-sm text-blue-600 font-semibold border border-gray-200"
                  : "text-gray-600 hover:bg-white hover:text-gray-800"
              }`}
            >
              <PlayCircle size={15} />
              {/* showNav implies scope === "active" (the catalogue is hidden in
                  archive), so this is always the live "Initiated" label. */}
              Initiated SOPs
              {instances.filter((i) => i.status === "IN_PROGRESS").length > 0 && (
                <span className="ml-auto text-[10px] bg-blue-500 text-white rounded-full px-1.5 py-0.5 font-bold">
                  {instances.filter((i) => i.status === "IN_PROGRESS").length}
                </span>
              )}
            </button>
          </div>
        )}

        {/* ── Content ── */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          {/* List */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <RowSkeleton count={6} />
            ) : section === "my_sops" ? (
              <>
                {filteredSops.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <ClipboardList size={32} className="mx-auto opacity-20 mb-2" />
                    <p className="text-sm">No SOPs assigned to your role.</p>
                    <p className="text-xs mt-1">SOPs where your authority has a step role appear here.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredSops.map((sop, idx) => (
                      <div
                        key={sop.id}
                        onClick={() => { setOpenPreviewId(sop.id); setOpenPreviewName(sop.process_name); }}
                        data-testid={`ROW_my_sop_${sop.id}_open`}
                        className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 hover:border-blue-200 hover:shadow-sm transition group cursor-pointer"
                      >
                        <span className="w-6 text-center text-xs font-semibold text-gray-400 flex-shrink-0">{idx + 1}</span>
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <FileText size={15} className="text-blue-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">{sop.process_name}</p>
                          <p className="text-[11px] text-gray-400">
                            {sop.process_code && <span className="mr-2">{sop.process_code}</span>}
                            {sop.step_count} step{sop.step_count !== 1 ? "s" : ""}
                            {sop.version && <span className="ml-2">v{sop.version}</span>}
                          </p>
                        </div>
                        {!readOnly && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenInitiateId(sop.id);
                              setOpenInitiateName(sop.process_name);
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white text-xs font-semibold rounded-lg hover:bg-blue-600 transition opacity-0 group-hover:opacity-100"
                          >
                            <PlayCircle size={13} />
                            Initiate
                          </button>
                        )}
                        <ChevronRight size={14} className="text-gray-300 group-hover:text-gray-500 transition" />
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {filteredInstances.length === 0 ? (
                  <NoDataFound
                    title="No SOP Instances"
                    message='Open "My SOPs" on your Identity page and click Initiate to start one.'
                  />
                ) : (
                  <div className="space-y-2">
                    {filteredInstances.map((inst) => {
                      const progress = Math.round(((inst.completed_count ?? 0) / Math.max(inst.step_count ?? 1, 1)) * 100);
                      return (
                        <div
                          key={inst.id}
                          className="bg-white border border-gray-200 rounded-xl px-4 py-3 hover:border-blue-200 hover:shadow-sm transition cursor-pointer"
                          onClick={() => setOpenInstanceId(inst.id)}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="flex-1 min-w-0 text-sm font-semibold text-gray-800 truncate">{inst.process_name}</p>
                                {/* Progress bar */}
                                <div className="step-progress-bar ml-auto">
                                  <div className="step-progress-bar__track">
                                    <div
                                      className={`step-progress-bar__fill ${
                                        inst.status === "COMPLETED" ? "bg-green-400" : "bg-blue-400"
                                      }`}
                                      style={{ width: `${progress}%` }}
                                    />
                                  </div>
                                  <span className="step-progress-bar__text">
                                    {inst.completed_count}/{inst.step_count}
                                  </span>
                                </div>
                                <Badge variant={statusVariant(inst.status)} size="sm" className="flex-shrink-0">
                                  {inst.status.replace(/_/g, " ")}
                                </Badge>
                              </div>
                              <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-400 mt-0.5">
                                {inst.location_name && <span>{inst.location_name}</span>}
                                {inst.population_name && <span>{inst.population_name}</span>}
                                <span>{inst.transaction_type}</span>
                                {inst.sample_text && <span>#{inst.sample_text}</span>}
                                {inst.initiated_by_name && <span>by {inst.initiated_by_name}</span>}
                              </div>
                            </div>
                            <ChevronRight size={14} className="text-gray-300 mt-1 flex-shrink-0" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MySopsTab;
